/**
 * Adapter nowego API listy podopiecznych: /patients/ranked.
 *
 * Koperta odpowiedzi jest ta sama co w /alarms i /reports, więc `readPage`
 * i `toQueryParams` bierzemy wprost z `alarms-api` — tutaj siedzi tylko to, czego
 * tamten adapter nie zna: kryteria porządkowania listy podopiecznych i kształt pola
 * z aktywnym alarmem.
 *
 * Czyste funkcje, bez zależności od Angulara — testują się bez TestBed.
 */

import { toCanonicalAlarmType } from '../alarms-api';

/** Filtry listy. `limit` 1–200; puste wartości wycina `toQueryParams`. */
export interface PatientsQuery {
  /** Fraza wyszukiwania: „imię nazwisko" ALBO fragment IMEI, podłańcuchowo. */
  q?: string;
  /** active | inactive — ten sam próg, którym liczą się kafelki. */
  status?: string;
  /** 'true' | 'false' — obecność opaski. */
  has_band?: string;
  /** Identyfikatory projektów po przecinku (`user.project`); OR między nimi. */
  project?: string;
  /** Kryteria po przecinku; rangę nadaje im serwer, nie kolejność na tej liście. */
  sort?: string;
  /** asc | desc — odwraca CAŁY łańcuch, alarm i aktywność także. */
  dir?: string;
  limit?: number;
  offset?: number;
}

/** Projekt w panelu filtrów i w plakietce przy nazwisku. */
export interface ProjectOption {
  id: number;
  name: string;
}

/**
 * Odpowiedź `/getProjects` → opcje filtra. Encja `Project` przechodzi przez JMS-a, więc
 * nazwa przychodzi jako `project_name`; `projectName` zostaje na wypadek innego mappera.
 * Wiersze bez id albo bez nazwy odpadają — nie da się po nich ani filtrować, ani ich podpisać.
 */
export function toProjectOptions(raw: any[]): ProjectOption[] {
  return (raw || [])
    .map(project => ({
      id: project && project.id != null ? Number(project.id) : NaN,
      name: project ? String(project.project_name || project.projectName || '').trim() : ''
    }))
    .filter(project => !isNaN(project.id) && project.name !== '')
    .sort((first, second) => first.name.localeCompare(second.name));
}

export interface PatientSortOption {
  /** Dokładnie ten klucz, który `/patients/ranked` przyjmuje w `sort`. */
  key: string;
  labelKey: string;
}

/**
 * Kryteria porządkowania listy: zaznaczone układają się w łańcuch, pierwsze rozstrzyga, kolejne
 * rozstrzygają remisy. Rangę podaje `/patients/ranked/order`; kolejność tutaj jest kopią
 * zapasową na czas, gdy ta odpowiedź jeszcze nie przyszła albo nie doszła.
 *
 * `labelKey` musi mieć tłumaczenie w komplecie 20 plików i18n, inaczej pokaże się surowy klucz.
 */
export const PATIENT_SORTS: PatientSortOption[] = [
  { key: 'alarm', labelKey: 'DASHBOARD.ACTIVE_ALARM' },
  { key: 'active', labelKey: 'NOTIFPANEL.BAND-ACTIVE' },
  { key: 'status', labelKey: 'BACKEND.BAND_WORN' },
  { key: 'last_measurement', labelKey: 'USER.LAST-MEASUREMENT-TEXT' },
  { key: 'name', labelKey: 'DASHBOARD.IMIE_I_NAZWISKO' },
  { key: 'battery_level', labelKey: 'USER.BATTERY-LEVEL' },
  { key: 'signal_strength', labelKey: 'TOOLTIP.SIGNAL-STRENGTH' }
];

/** Zaznaczone po każdym wejściu na listę. */
export const PATIENT_SORT_DEFAULTS = ['alarm', 'active'];

/** Kryteria z własnym nagłówkiem w widoku listy; alarm i aktywność wybiera się tylko w panelu. */
export const PATIENT_SORT_COLUMNS = ['status', 'last_measurement', 'name'];

export const PATIENT_FILTERS: { key: string; labelKey: string }[] = [
  { key: 'active', labelKey: 'NOTIFPANEL.BAND-ACTIVE' },
  { key: 'inactive', labelKey: 'SIDEPANEL.UNACTIVE' }
];

const PATIENT_FILTER_PAIRS = [
  ['active', 'inactive']
];

/** Zaznaczenia po dołożeniu `key`: odpada przeciwieństwo z pary. */
export function withPatientFilter(selection: string[], key: string): string[] {
  const para = PATIENT_FILTER_PAIRS.filter(pair => pair.indexOf(key) !== -1)[0] || [];

  return (selection || []).filter(item => para.indexOf(item) === -1).concat(key);
}

/** Zaznaczone filtry → parametry, którymi rozumie je serwer. */
export function toPatientFilterParams(filters: string[]): Pick<PatientsQuery, 'status'> {
  const wybrane = filters || [];
  const pierwszy = (...klucze: string[]) => klucze.filter(key => wybrane.indexOf(key) !== -1)[0] || '';

  return {
    status: pierwszy('active', 'inactive')
  };
}

/**
 * Zaznaczenia → łańcuch kryteriów w kolejności pierwszeństwa. Kolejność klikania nie ma
 * znaczenia, liczy się miejsce w `sorts`. Brak zaznaczeń znaczy „wszystkie" — komplet
 * i pustka dają ten sam porządek, dokładnie jak po stronie serwera.
 */
export function toPatientsSortChain(selection: string[], sorts: PatientSortOption[]): PatientSortOption[] {
  const chosen = selection || [];
  const chain = sorts.filter(option => chosen.indexOf(option.key) !== -1);

  return chain.length ? chain : sorts;
}

/**
 * Ranga z `/patients/ranked/order` → kryteria w kolejności, w której ustawia je serwer.
 * Kryterium bez tłumaczenia w panelu pomijamy — pokazałoby surowy klucz. Gdy odpowiedź
 * nic nie wnosi, zostaje kolejność z `PATIENT_SORTS`: panel działa dalej, tyle że bez
 * potwierdzenia z serwera.
 */
export function toPatientSorts(criteria: any[]): PatientSortOption[] {
  const ranked = (criteria || [])
    .slice()
    .sort((first, second) => first.rank - second.rank)
    .map(criterion => PATIENT_SORTS.filter(option => option.key === criterion.key)[0])
    .filter(option => option);

  return ranked.length ? ranked : PATIENT_SORTS;
}

/**
 * Typ kanoniczny z /alarms → dyskryminator encji. Backend nazywa ten sam alarm
 * dwojako: `sos` w filtrach list i `alarmsos` w polu `discr`. Szablon listy porównuje
 * się wyłącznie do tej drugiej postaci (`user?.alarm === 'alarmsos'`) i w niej przychodzi
 * też zdarzenie z WebSocketa, więc sprowadzamy do niej obie.
 */
const CANONICAL_TO_DISCRIMINATOR: { [canonical: string]: string } = {
  sos: 'alarmsos',
  fall: 'alarmfall',
  zone: 'alarmleavezone',
  call: 'alarmcall',
  immo: 'alarmimmo'
};

/**
 * Skleja dwie listy wierszy, odsiewając powtórzenia po `id`. Przy duplikacie wygrywa
 * wiersz z PIERWSZEJ listy — i na tym opierają się oba zastosowania:
 *
 *  - dociąganie w dół: `merge(dotychczasowe, nowaPorcja)`,
 *  - odświeżenie czubka po alarmie: `merge(swiezyCzubek, dotychczasowe)`.
 *
 * Odsiewanie jest tu konieczne, bo kolejność na serwerze zmienia się w czasie: nowy alarm
 * wynosi pacjenta na początek, wszyscy za nim przesuwają się o jedną pozycję i kolejna
 * porcja pobrana po `offset` zawiera wiersz, który już mamy. Bez odsiewu pokazałby się
 * dwa razy.
 *
 * Wiersze bez `id` przepuszczamy bez odsiewu — nie da się ich od siebie odróżnić, więc
 * każda próba deduplikacji albo by je gubiła, albo dublowała.
 */
export function mergePatientRows<T extends { id?: any }>(first: T[], second: T[]): T[] {
  const merged: T[] = [];
  const seen = new Set<string>();

  const append = (rows: T[]) => (rows || []).forEach(row => {
    if (!row) {
      return;
    }
    if (row.id === null || row.id === undefined) {
      merged.push(row);
      return;
    }
    const key = String(row.id);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  });

  append(first);
  append(second);

  return merged;
}

/**
 * Wiersz z API → wiersz, jakim posługuje się szablon.
 *
 * Jedyna realna zmiana kształtu to rozłożenie `active_alarm` na `alarm` i `alarmID`.
 * Te dwie nazwy są w komponencie od czasów, gdy alarm przychodził WYŁĄCZNIE
 * z WebSocketa (`setAlarms`), i czyta je szablon, `getAlarmPriority` oraz `openSos`.
 * Zmapowanie tutaj sprawia, że alarm z listy i alarm z socketa są tym samym
 * wierszem danych — bez rozgałęziania w każdym z tych miejsc.
 *
 * Alarm bez identyfikatora pomijamy w całości: `openSos` nawiguje na
 * `/users/sos-request/{alarmID}`, więc sam `alarm` bez `alarmID` dałby przejście
 * pod `/sos-request/undefined`.
 */
export function normalizePatientRow(raw: any): any {
  if (!raw) {
    return {};
  }

  const row = { ...raw };
  const alarm = raw.active_alarm;

  if (alarm && alarm.id != null) {
    const canonical = toCanonicalAlarmType(alarm.type);
    row.alarm = CANONICAL_TO_DISCRIMINATOR[canonical] || canonical;
    row.alarmID = alarm.id;
    // Znacznik źródła. Plakietki mają teraz DWA źródła — serwer i WebSocket — a gałąź
    // kasująca w `setAlarms()` powstała, gdy było jedno, i zdejmuje plakietkę każdemu,
    // kogo nie ma na liście socketa. Bez tego znacznika obsłużenie jednego alarmu
    // z socketa gasiłoby po cichu plakietki wszystkich NIEPOWIĄZANYCH pacjentów,
    // których alarm przyszedł z serwera.
    row.alarmFromServer = true;
  }

  return row;
}
